import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router";
import axios from "axios";

const SurveyForm = ({ surveyData }) => {
  console.log("Survey Data", surveyData);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [surveyStarted, setSurveyStarted] = useState(false);

  const { id: surveyId } = useParams();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("user_id");
  const navigate = useNavigate();

  const questions = surveyData.data?.questions || [];
  const currentQuestion = questions[currentQuestionIndex];

  // Start survey function
  const startSurvey = () => {
    setSurveyStarted(true);
  };

  // (progress UI computed when needed) - removed unused showProgress variable

  // Get default value for a question type
  const getDefaultValue = (question) => {
    switch (question.question_type) {
      case "multiple_choice":
        return question.options && question.options.length > 0
          ? question.options[0].value
          : "";
      case "rating":
        return question.min_value ? question.min_value.toString() : "1";
      case "yes_no":
        return "yes";
      case "scale":
        return question.min_value ? question.min_value.toString() : "1";
      case "text":
        return "";
      default:
        return "";
    }
  };

  // Handle input changes for all question types
  const handleInputChange = (questionId, value) => {
    setFormData((prev) => ({
      ...prev,
      [questionId]: value,
    }));

    // Clear error when user starts typing/selecting
    if (errors[questionId]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  // Set default value when question becomes active
  useEffect(() => {
    if (currentQuestion && !formData[currentQuestion.question_id]) {
      const defaultValue = getDefaultValue(currentQuestion);
      if (defaultValue) {
        setFormData((prev) => ({
          ...prev,
          [currentQuestion.question_id]: defaultValue,
        }));
      }
    }
  }, [currentQuestionIndex, currentQuestion, formData]);

  // Navigate to next question (manual)
  const goToNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Navigate to previous question (manual)
  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Validate current question
  const validateCurrentQuestion = () => {
    if (
      currentQuestion.required &&
      (!formData[currentQuestion.question_id] ||
        formData[currentQuestion.question_id] === "")
    ) {
      setErrors((prev) => ({
        ...prev,
        [currentQuestion.question_id]: "This question is required",
      }));
      return false;
    }
    return true;
  };

  // Handle next question (manual)
  const handleNext = () => {
    if (validateCurrentQuestion()) {
      goToNextQuestion();
    }
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};

    questions.forEach((question) => {
      if (
        question.required &&
        (!formData[question.question_id] ||
          formData[question.question_id] === "")
      ) {
        newErrors[question.question_id] = "This question is required";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission (manual)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);

      try {
        // Create a copy of the survey data structure
        const surveyPayload = {
          data: {
            topic: surveyData.data.topic,
            description: surveyData.data.description,
            questions: surveyData.data.questions.map((question) => ({
              ...question,
              // Replace null answer with user's selected answer
              answer: formData[question.question_id] || null,
            })),
            estimated_time: surveyData.data.estimated_time,
          },
        };

        console.log("Submitting survey payload:", surveyPayload);

        const answers = {};
        Object.keys(formData).forEach((questionId) => {
          answers[questionId] = formData[questionId];
        });

        // Use survey_id from surveyData if available, otherwise use URL param
        const finalSurveyId = surveyData.survey_id || surveyId;

        // First API call - Submit answers
        const submitPayload = {
          user_id: userId,
          survey_id: finalSurveyId,
          answers: answers,
        };

        const submitResponse = await axios.post(
          "https://pulse.upstartaec.com/agent/survey-generation/submit-answers",
          submitPayload
        );

        console.log(
          "Survey answers submitted successfully:",
          submitResponse.data
        );

        // Second API call - Analyze survey
        const analyzePayload = {
          user_id: userId,
          data: {
            survey_id: finalSurveyId,
            topic: surveyData.data.topic,
            description: surveyData.data.description,
            questions: surveyData.data.questions.map((question) => ({
              ...question,
              // Replace null answer with user's selected answer
              answer: formData[question.question_id] || null,
            })),
            estimated_time: surveyData.data.estimated_time,
          },
        };

        console.log("Analyzing survey payload:", analyzePayload);

        const analyzeResponse = await axios.post(
          "https://pulse.upstartaec.com/agent/survey-generation/analyze-survey",
          analyzePayload
        );

        console.log("Survey analyzed successfully:", analyzeResponse.data);

        // Navigate to analysis page with the response data
        navigate("/survey-analysis", {
          state: { analysisData: analyzeResponse.data },
        });
      } catch (error) {
        console.error("Error processing survey:", error);
        console.error("Error response:", error.response?.data);

        if (error.response?.status === 410) {
          alert(
            "Survey has expired and is no longer available. Please contact the survey administrator for a new link."
          );
        } else {
          alert("Failed to submit survey. Please try again.");
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      alert("Please fill in all required fields.");
    }
  };

  // Render different input types based on question type
  const renderQuestionInput = (question) => {
    switch (question.question_type) {
      case "multiple_choice":
        return (
          <div className="space-y-1.5 sm:space-y-2 lg:space-y-3">
            {question.options
              .filter(
                (option) =>
                  option.text.toLowerCase() !== "n/a" &&
                  option.value.toLowerCase() !== "n/a"
              )
              .map((option, index) => (
                <div key={option.value} className="relative">
                  <label className="block cursor-pointer">
                    <input
                      type="radio"
                      name={`question-${question.question_id}`}
                      value={option.value}
                      checked={formData[question.question_id] === option.value}
                      onChange={() =>
                        handleInputChange(question.question_id, option.value)
                      }
                      className="absolute opacity-0 w-0 h-0"
                    />
                    <div
                      className={`p-2.5 sm:p-3 lg:p-4 bg-white border-2 rounded-lg transition-all duration-200 ${
                        formData[question.question_id] === option.value
                          ? "border-black bg-white font-semibold"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      <span className={`font-medium text-xs sm:text-xs lg:text-sm text-gray-900`}>
                        {option.text}
                      </span>
                    </div>
                  </label>
                </div>
              ))}
          </div>
        );

      case "rating":
        return (
          <div className="text-center w-full mt-4 sm:mt-5 lg:mt-6">
            <div className="flex flex-col items-center gap-3 sm:gap-4 max-w-md mx-auto">
              <div className="relative w-full h-6 sm:h-8 flex items-center">
                <input
                  type="range"
                  min={question.min_value}
                  max={question.max_value}
                  value={formData[question.question_id] || question.min_value}
                  onChange={(e) =>
                    handleInputChange(question.question_id, e.target.value)
                  }
                  className="w-full h-2 sm:h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>
              <div className="flex justify-between items-center w-full text-xs sm:text-sm text-gray-500 font-medium">
                <span className="text-gray-400">{question.min_value}</span>
                <span className="text-sm sm:text-base lg:text-lg font-semibold text-white bg-black px-2 py-1 rounded-full min-w-[30px] sm:min-w-[40px] text-center">
                  {formData[question.question_id] || question.min_value}
                </span>
                <span className="text-gray-400">{question.max_value}</span>
              </div>
            </div>
          </div>
        );

      case "text":
        return (
          <div className="relative w-full mt-4 sm:mt-5 lg:mt-6">
            <textarea
              className="w-full p-3 sm:p-4 lg:p-5 border border-gray-300 rounded-lg text-xs sm:text-sm lg:text-base bg-white transition-all duration-200 font-inherit resize-vertical min-h-[60px] sm:min-h-[80px] lg:min-h-[100px] text-gray-700 focus:outline-none focus:border-black focus:ring-2 focus:ring-gray-200"
              placeholder={question.placeholder || "Type your answer here..."}
              value={formData[question.question_id] || ""}
              onChange={(e) =>
                handleInputChange(question.question_id, e.target.value)
              }
              rows={3}
            />
          </div>
        );

      case "yes_no":
        return (
          <div className="flex gap-3 sm:gap-4 lg:gap-6 justify-center w-full max-w-sm mx-auto mt-4 sm:mt-5 lg:mt-6">
            <label className="relative cursor-pointer flex-1">
              <input
                type="radio"
                name={`question-${question.question_id}`}
                value="yes"
                checked={formData[question.question_id] === "yes"}
                onChange={() => handleInputChange(question.question_id, "yes")}
                className="absolute opacity-0 w-0 h-0"
              />
              <div
                className={`p-3 sm:p-4 lg:p-5 bg-white border-2 rounded-lg transition-all duration-200 text-xs sm:text-sm lg:text-base text-center min-h-[40px] sm:min-h-[50px] lg:min-h-[60px] flex items-center justify-center w-full box-border ${
                  formData[question.question_id] === "yes"
                    ? "border-black bg-white font-semibold"
                    : "border-gray-200 text-gray-600 font-normal"
                }`}
              >
                <span className="text-gray-900 text-xs sm:text-xs lg:text-sm">Yes</span>
              </div>
            </label>
            <label className="relative cursor-pointer flex-1">
              <input
                type="radio"
                name={`question-${question.question_id}`}
                value="no"
                checked={formData[question.question_id] === "no"}
                onChange={() => handleInputChange(question.question_id, "no")}
                className="absolute opacity-0 w-0 h-0"
              />
              <div
                className={`p-3 sm:p-4 lg:p-5 bg-white border-2 rounded-lg transition-all duration-200 text-xs sm:text-sm lg:text-base text-center min-h-[40px] sm:min-h-[50px] lg:min-h-[60px] flex items-center justify-center w-full box-border ${
                  formData[question.question_id] === "no"
                    ? "border-black bg-white font-semibold"
                    : "border-gray-200 text-gray-600 font-normal"
                }`}
              >
                <span className="text-gray-900 text-xs sm:text-xs lg:text-sm">No</span>
              </div>
            </label>
          </div>
        );

      case "scale":
        return (
          <div className="text-center w-full mt-4 sm:mt-5 lg:mt-6">
            <div className="flex flex-col items-center gap-3 sm:gap-4 max-w-md mx-auto">
              <div className="relative w-full h-6 sm:h-8 flex items-center">
                <input
                  type="range"
                  min={question.min_value}
                  max={question.max_value}
                  value={formData[question.question_id] || question.min_value}
                  onChange={(e) =>
                    handleInputChange(question.question_id, e.target.value)
                  }
                  className="w-full h-2 sm:h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>
              <div className="flex justify-between items-center w-full text-xs sm:text-sm text-gray-500 font-medium">
                <span className="text-gray-400">{question.min_value}</span>
                <span className="text-sm sm:text-base lg:text-lg font-semibold text-white bg-black px-2 py-1 rounded-full min-w-[30px] sm:min-w-[40px] text-center">
                  {formData[question.question_id] || question.min_value}
                </span>
                <span className="text-gray-400">{question.max_value}</span>
              </div>
              {/* Show scale labels if available */}
              {question.scale_labels && question.scale_labels.length > 0 && (
                <div className="flex justify-between items-center w-full text-xs text-gray-400 mt-2">
                  <span className="text-left">{question.scale_labels[0]}</span>
                  <span className="text-right">
                    {question.scale_labels[question.scale_labels.length - 1]}
                  </span>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return <div className="text-red-500">Unsupported question type</div>;
    }
  };

  if (!surveyData.data) {
    return (
      <div className="min-h-screen bg-white p-3 sm:p-4 lg:p-6 font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif] relative">
        <div className="flex flex-col items-center justify-center min-h-[250px] sm:min-h-[300px] text-gray-500 text-center">
          <div className="w-8 h-8 sm:w-10 sm:h-10 border-3 border-gray-200 border-t-black rounded-full animate-spin mb-3 sm:mb-4"></div>
          <p className="text-xs sm:text-sm lg:text-base">Loading survey...</p>
        </div>
      </div>
    );
  }

  // Check if survey has questions
  if (!questions || questions.length === 0) {
    return (
      <div className="min-h-screen bg-white p-3 sm:p-4 lg:p-6 font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif] relative">
        <div className="flex flex-col items-center justify-center min-h-[250px] sm:min-h-[300px] text-gray-500 text-center">
          <div className="text-red-500 mb-4 sm:mb-6">
            <svg
              className="w-12 h-12 sm:w-16 sm:h-16"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900 mb-3 sm:mb-4">
            Survey Not Available
          </h2>
          <p className="text-xs sm:text-sm lg:text-base text-gray-600 leading-relaxed max-w-sm mx-auto px-3">
            This survey appears to be empty or no longer available. Please
            contact the survey administrator.
          </p>
        </div>
      </div>
    );
  }

  // Show landing page if survey hasn't started
  if (!surveyStarted) {
    return (
      <div className="min-h-screen bg-white p-3 sm:p-4 lg:p-6 font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif] relative">
        <div className="max-w-xl mx-auto pt-6 sm:pt-8 lg:pt-10">
          {/* Landing Page Header */}
          <div className="text-center mb-8 sm:mb-10 lg:mb-12 animate-fadeInUp">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-black rounded-xl flex items-center justify-center mx-auto mb-4 sm:mb-6 text-white shadow-lg">
              <svg
                className="w-6 h-6 sm:w-8 sm:h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-gray-900 mb-3 sm:mb-4 leading-tight px-3">
              {surveyData.data.topic}
            </h1>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 leading-relaxed mb-4 sm:mb-6 max-w-md mx-auto px-3">
              {surveyData.data.description}
            </p>
            <div className="flex justify-center gap-2 sm:gap-3 lg:gap-4 flex-wrap px-3">
              <span className="flex items-center gap-1 px-3 sm:px-4 py-1 sm:py-2 bg-gray-50 rounded-full text-xs sm:text-sm text-gray-600 font-medium border border-gray-200">
                <svg
                  className="w-3 h-3 sm:w-4 sm:h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                60 - 90 seconds
              </span>
              <span className="flex items-center gap-1 px-3 sm:px-4 py-1 sm:py-2 bg-gray-50 rounded-full text-xs sm:text-sm text-gray-600 font-medium border border-gray-200">
                <svg
                  className="w-3 h-3 sm:w-4 sm:h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {questions.length} questions
              </span>
            </div>
          </div>

          {/* Start Survey Button */}
          <div className="text-center mt-8 sm:mt-10 lg:mt-12">
            <button
              onClick={startSurvey}
              className="bg-black text-white border-none rounded-xl py-3 sm:py-4 lg:py-5 px-6 sm:px-8 lg:px-10 text-base sm:text-lg lg:text-xl font-semibold cursor-pointer transition-all duration-200 inline-flex items-center gap-2 sm:gap-3 min-w-[140px] sm:min-w-[160px] lg:min-w-[180px] justify-center shadow-lg hover:bg-gray-800 hover:-translate-y-1 hover:shadow-xl"
            >
              Start Survey
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif] relative flex items-center justify-center">
      <div className="w-full max-w-lg mx-auto px-3 sm:px-4 py-3 sm:py-4 lg:py-6">
        {/* Progress Bar */}
        <div className="mb-3 sm:mb-4 lg:mb-6">
          <div className="flex justify-end items-center mb-1 sm:mb-2">
            <div className="text-xs sm:text-sm lg:text-base text-gray-500">
              {currentQuestionIndex + 1} of {questions.length}
            </div>
          </div>
          <div className="w-full h-1 sm:h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all duration-500"
              style={{
                width: `${
                  ((currentQuestionIndex + 1) / questions.length) * 100
                }%`,
              }}
            ></div>
          </div>
        </div>
        {/* Question Container with Animation */}
        <div className="mb-4 sm:mb-6 min-h-[40vh] sm:min-h-[35vh] lg:min-h-[30vh] relative overflow-hidden">
          <div key={currentQuestionIndex} className="animate-slideInUp">
            <h2 className="text-base sm:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 lg:mb-6 leading-tight">
              {currentQuestion?.question_text}
              {currentQuestion?.required && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </h2>

            {/* Answer Options */}
            {currentQuestion && renderQuestionInput(currentQuestion)}

            {errors[currentQuestion?.question_id] && (
              <div className="mt-3 sm:mt-4 text-red-500 text-xs sm:text-sm">
                {errors[currentQuestion.question_id]}
              </div>
            )}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-end gap-2 sm:gap-3 mt-10 sm:mt-12">
          {currentQuestionIndex > 0 && (
            <button
              type="button"
              onClick={goToPreviousQuestion}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-1.5 sm:py-2 lg:py-3 px-2 sm:px-3 lg:px-4 rounded-lg transition-all duration-200 flex items-center gap-1 sm:gap-2 text-xs sm:text-sm lg:text-base"
            >
              <svg
                className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Previous
            </button>
          )}
          {currentQuestionIndex < questions.length - 1 ? (
            <button
              type="button"
              onClick={handleNext}
              className="bg-black hover:bg-gray-800 text-white font-semibold py-1.5 sm:py-2 lg:py-3 px-2 sm:px-3 lg:px-4 rounded-lg transition-all duration-200 flex items-center gap-1 sm:gap-2 text-xs sm:text-sm lg:text-base shadow-lg"
            >
              Next
              <svg
                className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          ) : (
            <button
              type="submit"
              onClick={handleSubmit}
              className={`bg-black hover:bg-gray-800 text-white font-semibold py-1.5 sm:py-2 lg:py-3 px-2 sm:px-3 lg:px-4 rounded-lg transition-all duration-200 flex items-center gap-1 sm:gap-2 text-xs sm:text-sm lg:text-base shadow-lg ${
                isSubmitting ? "opacity-70 cursor-not-allowed" : ""
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin"></div>
                  <span className="hidden sm:inline">Submitting...</span>
                  <span className="sm:hidden">Submit...</span>
                </>
              ) : (
                <>
                  <span className="hidden sm:inline">Submit Survey</span>
                  <span className="sm:hidden">Submit</span>
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slideInUp {
          animation: slideInUp 0.5s ease-out;
        }

        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shake {
          0%,
          100% {
            transform: translateX(0);
          }
          25% {
            transform: translateX(-5px);
          }
          75% {
            transform: translateX(5px);
          }
        }

        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }

        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out;
        }

        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }

        .slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #000000;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
        }

        .slider::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #000000;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
        }

        .slider::-moz-range-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
        }

        .slider::-webkit-slider-track {
          background: linear-gradient(
            to right,
            #000000 0%,
            #000000 50%,
            #e5e7eb 50%,
            #e5e7eb 100%
          );
          border-radius: 4px;
          height: 8px;
        }

        .slider::-moz-range-track {
          background: linear-gradient(
            to right,
            #000000 0%,
            #000000 50%,
            #e5e7eb 50%,
            #e5e7eb 100%
          );
          border-radius: 4px;
          height: 8px;
          border: none;
        }

        /* Responsive breakpoints */
        @media (max-width: 640px) {
          .slider::-webkit-slider-thumb {
            width: 20px;
            height: 20px;
          }

          .slider::-moz-range-thumb {
            width: 20px;
            height: 20px;
          }
        }

        @media (min-width: 641px) and (max-width: 768px) {
          .slider::-webkit-slider-thumb {
            width: 26px;
            height: 26px;
          }

          .slider::-moz-range-thumb {
            width: 26px;
            height: 26px;
          }
        }

        @media (min-width: 769px) {
          .slider::-webkit-slider-thumb {
            width: 28px;
            height: 28px;
          }

          .slider::-moz-range-thumb {
            width: 28px;
            height: 28px;
          }
        }
      `}</style>
    </div>
  );
};

export default SurveyForm;
